import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import {
  AppointmentOptions,
  AppointmentsListResponseDto,
  AppointmentResponseDto,
  AppointmentSettingsDto,
  CancelAppointmentDto,
  CompleteAppointmentDto,
  CreateAppointmentByExpertDto,
  CreateTimeOffDto,
  RescheduleAppointmentDto,
  TimeOffResponseDto,
  UpdateScheduleDto,
  WorkingHoursDto,
  AppointmentStatus,
} from '../types/appointment.types';

interface GetAppointmentsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: AppointmentStatus | '';
  clientId?: number;
  expertId?: number;
}

class AppointmentService {
  async getOptions(panel?: 'client' | 'expert', clientId?: number): Promise<AppointmentOptions> {
    const response = await api.get<ApiResponse<AppointmentOptions>>('/appointments/options', {
      headers: panel ? { 'X-Panel': panel } : undefined,
      params: clientId ? { clientId } : undefined,
    });
    return unwrapApiResponse<AppointmentOptions>(response);
  }

  async getMyAppointments(params: GetAppointmentsParams = {}): Promise<AppointmentsListResponseDto> {
    const response = await api.get('/appointments/my', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        status: params.status || undefined,
        expertId: params.expertId || undefined,
      },
    });
    return response.data;
  }

  async getExpertAppointments(params: GetAppointmentsParams = {}): Promise<AppointmentsListResponseDto> {
    const response = await api.get('/appointments/expert', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search || undefined,
        status: params.status || undefined,
        clientId: params.clientId || undefined,
      },
    });
    return response.data;
  }

  async getAppointmentDetails(id: number): Promise<AppointmentResponseDto> {
    const response = await api.get<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}`);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async confirmAppointment(id: number): Promise<AppointmentResponseDto> {
    const response = await api.patch<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}/confirm`);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async cancelAppointment(id: number, data: CancelAppointmentDto): Promise<AppointmentResponseDto> {
    const response = await api.delete<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}`, {
      data,
    });
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async createAppointmentByExpert(data: CreateAppointmentByExpertDto): Promise<AppointmentResponseDto> {
    const response = await api.post<ApiResponse<AppointmentResponseDto>>('/appointments', data);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async submitReview(id: number, data: { rating: number; comment?: string }): Promise<AppointmentResponseDto> {
    const response = await api.post<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}/review`, data);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async rescheduleAppointment(id: number, data: RescheduleAppointmentDto): Promise<AppointmentResponseDto> {
    const response = await api.patch<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}/reschedule`, data);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async completeAppointment(id: number, data: CompleteAppointmentDto): Promise<AppointmentResponseDto> {
    const response = await api.patch<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}/complete`, data);
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async setOutcome(id: number, outcome: string): Promise<AppointmentResponseDto> {
    const response = await api.patch<ApiResponse<AppointmentResponseDto>>(`/appointments/${id}/outcome`, { outcome });
    return unwrapApiResponse<AppointmentResponseDto>(response);
  }

  async getExpertSchedule(): Promise<WorkingHoursDto[]> {
    const response = await api.get<ApiResponse<WorkingHoursDto[]>>('/expert/schedule');
    return unwrapApiResponse<WorkingHoursDto[]>(response);
  }

  async updateExpertSchedule(data: UpdateScheduleDto): Promise<{ success: boolean; message: string }> {
    const response = await api.patch('/expert/schedule', data);
    return response.data;
  }

  async getAppointmentSettings(): Promise<AppointmentSettingsDto> {
    const response = await api.get<ApiResponse<AppointmentSettingsDto>>('/expert/appointment-settings');
    return unwrapApiResponse<AppointmentSettingsDto>(response);
  }

  async updateAppointmentSettings(data: AppointmentSettingsDto): Promise<{ success: boolean; message: string }> {
    const response = await api.patch('/expert/appointment-settings', data);
    return response.data;
  }

  async getTimeOffPeriods(): Promise<TimeOffResponseDto[]> {
    const response = await api.get<ApiResponse<TimeOffResponseDto[]>>('/expert/time-off');
    return unwrapApiResponse<TimeOffResponseDto[]>(response);
  }

  async createTimeOffPeriod(data: CreateTimeOffDto): Promise<TimeOffResponseDto> {
    const response = await api.post<ApiResponse<TimeOffResponseDto>>('/expert/time-off', data);
    return unwrapApiResponse<TimeOffResponseDto>(response);
  }

  async deleteTimeOffPeriod(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/expert/time-off/${id}`);
    return response.data;
  }

}

export const appointmentService = new AppointmentService();
